/**
 * Safe localStorage helper.
 *
 * localStorage is a single shared bucket (~5 MB on most browsers). If any
 * key in the app (typically a base64-encoded logo on the branding cache)
 * overflows the quota, every subsequent `setItem` in ANY module fails
 * with QuotaExceededError — including the small theme-colours cache
 * that powers the admin colour editor's persistence across refreshes.
 *
 * This helper, on quota failure, evicts known large / least-critical
 * keys and retries once. If it still fails, the write is silently
 * skipped (no crash) so the app continues to function.
 */

// Keys we can safely drop to free space — ordered largest/most-expendable
// first. These are runtime caches that can always be re-fetched from the
// backend, so losing them is harmless.
const EVICTION_ORDER = [
  'ccuk_branding',     // usually the biggest offender — may contain base64 logos
  'ccuk_promo_banner',
  'ccuk_typography',
];

export function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    const name = err?.name || '';
    const isQuota =
      name === 'QuotaExceededError' ||
      name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err?.code === 22 ||
      err?.code === 1014;
    if (!isQuota) return false;

    // Evict in order, retrying after each removal. Skip the key we're
    // trying to write so we don't clear our own data.
    for (const evictKey of EVICTION_ORDER) {
      if (evictKey === key) continue;
      try {
        localStorage.removeItem(evictKey);
        localStorage.setItem(key, value);
        return true;
      } catch {
        // still no room, try the next eviction candidate
      }
    }

    // Last resort — clear everything we own (anything ccuk_*) and retry.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('ccuk_') && k !== key) {
          localStorage.removeItem(k);
        }
      }
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }
}
